import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { REACTIONS } from '../lib/reactions'
import { useAuth } from '../context/AuthContext'
import RankBadge from './RankBadge'

// InlineFeedPost — pure card view only
// Clicking anywhere opens the PostModal via onOpen prop
export default function InlineFeedPost({ post, onDelete, onOpen }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [visible, setVisible] = useState(false)
  const postRef = useRef(null)

  // Entrance animation on scroll into view
  useEffect(() => {
    if (!postRef.current) return
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect() } },
      { threshold: 0.04 }
    )
    obs.observe(postRef.current)
    return () => obs.disconnect()
  }, [])

  const date = new Date(post.created_at).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric'
  })

  function open(e) {
    e.stopPropagation()
    onOpen?.(post.id)
  }

  return (
    <article
      ref={postRef}
      className={`ifp ${visible ? 'ifp-visible' : ''}`}
      onClick={() => onOpen?.(post.id)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onOpen?.(post.id)}
      style={{ cursor: 'pointer' }}
    >
      {/* Cover image thumbnail */}
      {post.cover_image && (
        <div
          className="ifp-cover"
          style={{ backgroundImage: `url(${post.cover_image})` }}
        >
          <div className="ifp-cover-gradient" />
          <div className="ifp-cover-read-hint">
            <span>Read</span>
            <span className="ifp-cover-read-arrow">↓</span>
          </div>
        </div>
      )}

      {/* Card header */}
      <div className="ifp-header">
        <div className="ifp-header-top">
          <span className="ifp-tag">{post.tag || 'the feed'}</span>
          {post.featured && <span className="ifp-featured">Featured</span>}
          {post.is_editorial && <span className="ifp-editorial">◆ Editorial Pick</span>}
          {post.status !== 'published' && (
            <span className={`status-badge status-${post.status}`}>{post.status}</span>
          )}
        </div>

        <h2 className="ifp-title">{post.title}</h2>

        {post.excerpt && (
          <p className="ifp-excerpt">{post.excerpt}</p>
        )}

        <div className="ifp-meta">
          <button
            className="ifp-author"
            onClick={e => { e.stopPropagation(); navigate(`/writer/${post.profiles?.id || post.author_id}`) }}
          >
            <span className="ifp-author-avatar">
              {post.is_anonymous ? '?' : post.profiles?.display_name?.[0]?.toUpperCase()}
            </span>
            <span className="ifp-author-name">
              {post.is_anonymous ? 'Anonymous' : post.profiles?.display_name}
            </span>
            {!post.is_anonymous && (post.profiles?.column_inches > 0 || post.profiles?.is_voice) && (
              <RankBadge
                columnInches={post.profiles?.column_inches || 0}
                isVoice={post.profiles?.is_voice || false}
                size="sm"
              />
            )}
          </button>
          <div className="ifp-meta-right">
            {post.read_time && (
              <span className="ifp-read-time">
                <span className="ifp-read-time-dot">◌</span>
                {post.read_time} min
              </span>
            )}
            <span className="ifp-date">{date}</span>
            <button className="ifp-open-btn" onClick={open}>
              Read
              <span className="ifp-open-btn-arrow">↗</span>
            </button>
          </div>
        </div>
      </div>
    </article>
  )
}
